"use client"
import Link from "next/link"
import { useEffect, useState } from "react"
import { useParams } from "next/navigation"
import { jsonLdScript } from "@/lib/jsonld"

// ── Lightweight markdown renderer (no library needed) ──────────────────────
function renderInline(text: string): React.ReactNode {
  // Bold **text** and italic *text*
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g)
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**"))
      return <strong key={i}>{part.slice(2, -2)}</strong>
    if (part.startsWith("*") && part.endsWith("*"))
      return <em key={i}>{part.slice(1, -1)}</em>
    return part
  })
}

function renderBody(body: string) {
  const lines = body.split("\n")
  const elements: React.ReactNode[] = []
  let listItems: string[] = []
  let key = 0

  function flushList() {
    if (listItems.length === 0) return
    elements.push(
      <ul key={key++} style={{ paddingLeft: 24, marginBottom: 20, lineHeight: 1.8 }}>
        {listItems.map((item, i) => (
          <li key={i} style={{ marginBottom: 6 }}>{renderInline(item)}</li>
        ))}
      </ul>
    )
    listItems = []
  }

  for (const raw of lines) {
    const line = raw.trimEnd()

    // Horizontal rule
    if (/^---+$/.test(line.trim())) {
      flushList()
      elements.push(<hr key={key++} style={{ border: "none", borderTop: "1px solid var(--line)", margin: "32px 0" }} />)
      continue
    }

    // H1
    if (line.startsWith("# ") && !line.startsWith("## ")) {
      flushList()
      elements.push(<h2 key={key++} style={{ fontFamily: "Fraunces, serif", fontSize: 28, fontWeight: 700, color: "var(--navy)", marginBottom: 12, marginTop: 36 }}>{renderInline(line.slice(2))}</h2>)
      continue
    }

    // H2
    if (line.startsWith("## ") && !line.startsWith("### ")) {
      flushList()
      elements.push(<h3 key={key++} style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 700, color: "var(--navy)", marginBottom: 10, marginTop: 32 }}>{renderInline(line.slice(3))}</h3>)
      continue
    }

    // H3
    if (line.startsWith("### ")) {
      flushList()
      elements.push(<h4 key={key++} style={{ fontSize: 17, fontWeight: 700, color: "var(--navy)", marginBottom: 8, marginTop: 24 }}>{renderInline(line.slice(4))}</h4>)
      continue
    }

    // Bullet list item
    if (/^[-*] /.test(line)) {
      listItems.push(line.slice(2))
      continue
    }

    // Numbered list item
    if (/^\d+\. /.test(line)) {
      listItems.push(line.replace(/^\d+\. /, ""))
      continue
    }

    // Inline image: ![alt text](url)
    const imgMatch = line.match(/^!\[([^\]]*)\]\(([^)]+)\)$/)
    if (imgMatch) {
      flushList()
      const [, alt, src] = imgMatch
      elements.push(
        <figure key={key++} style={{ margin: "32px 0" }}>
          <img
            src={src}
            alt={alt}
            style={{ width: "100%", borderRadius: 10, display: "block", maxHeight: 480, objectFit: "cover" }}
          />
          {alt && (
            <figcaption style={{ textAlign: "center", fontSize: 13, color: "var(--muted)", marginTop: 8, fontStyle: "italic" }}>
              {alt}
            </figcaption>
          )}
        </figure>
      )
      continue
    }

    // Blank line flushes list
    if (!line.trim()) {
      flushList()
      continue
    }

    // Regular paragraph
    flushList()
    elements.push(<p key={key++} style={{ marginBottom: 20 }}>{renderInline(line)}</p>)
  }

  flushList()
  return elements
}

export default function BlogPostPage() {
  const params = useParams()
  const [post, setPost] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    fetch("/api/blog/" + params.slug)
      .then(r => r.json())
      .then(d => {
        if (d.success) setPost(d.data)
        else setNotFound(true)
        setLoading(false)
      })
      .catch(() => { setNotFound(true); setLoading(false) })
  }, [params.slug])

  if (loading) return <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--cream)" }}><div style={{ fontFamily: "Fraunces, serif", fontSize: 20, color: "var(--navy)", opacity: 0.5 }}>Loading...</div></div>

  if (notFound || !post) return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--cream)" }}>
      <div style={{ textAlign: "center" }}>
        <h1 style={{ fontFamily: "Fraunces, serif", fontSize: 32, color: "var(--navy)", marginBottom: 16 }}>Post not found</h1>
        <Link href="/blog" style={{ color: "var(--gold-deep)", fontWeight: 600 }}>Back to blog</Link>
      </div>
    </div>
  )

  const SITE = "https://zimestates.co.zw"

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt || post.metaDescription || post.title,
    ...(post.coverImage ? { image: [post.coverImage] } : {}),
    datePublished: post.publishedAt,
    dateModified: post.updatedAt ?? post.publishedAt,
    author: { "@type": "Organization", name: "Zim Estates", url: SITE },
    publisher: {
      "@type": "Organization",
      name: "Zim Estates",
      url: SITE,
      logo: { "@type": "ImageObject", url: SITE + "/og-image-1200x630.png" },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": SITE + "/blog/" + post.slug },
  }

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home",   item: SITE },
      { "@type": "ListItem", position: 2, name: "Blog",   item: SITE + "/blog" },
      { "@type": "ListItem", position: 3, name: post.title, item: SITE + "/blog/" + post.slug },
    ],
  }

  return (
    <div style={{ background: "var(--cream)", minHeight: "100vh" }}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLdScript(articleLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLdScript(breadcrumbLd) }} />
      {/* Hero, cover image if available, otherwise navy gradient */}
      <div style={post.coverImage ? {
        backgroundImage: `linear-gradient(to bottom, rgba(27,45,84,0.72) 0%, rgba(27,45,84,0.55) 100%), url(${post.coverImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        padding: "80px 24px 64px",
      } : {
        background: "var(--navy)",
        padding: "80px 24px 64px",
      }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <Link href="/blog" style={{ color: "rgba(253,251,247,0.6)", fontSize: 13, textDecoration: "none", marginBottom: 24, display: "inline-block" }}>← Back to blog</Link>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--gold)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 16 }}>{post.category}</div>
          <h1 style={{ fontFamily: "Fraunces, serif", fontSize: 40, fontWeight: 700, color: "var(--cream)", lineHeight: 1.2, marginBottom: 20 }}>{post.title}</h1>
          {(post.excerpt || post.metaDescription) && (
            <p style={{ color: "rgba(253,251,247,0.8)", fontSize: 17, lineHeight: 1.6, marginBottom: 20, maxWidth: 600 }}>{post.excerpt || post.metaDescription}</p>
          )}
          <div style={{ display: "flex", gap: 16, color: "rgba(253,251,247,0.6)", fontSize: 13 }}>
            <span>{new Date(post.publishedAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</span>
            <span>·</span>
            <span>{Math.ceil(post.body.split(" ").length / 200)} min read</span>
          </div>
        </div>
      </div>
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "48px 24px" }}>
        <div style={{ fontSize: 16, lineHeight: 1.8, color: "var(--ink)" }}>
          {renderBody(post.body)}
        </div>
        <div style={{ marginTop: 48, padding: 24, background: "var(--cream-warm)", border: "1px solid var(--line)", borderRadius: 14 }}>
          <div style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 600, color: "var(--navy)", marginBottom: 8 }}>Looking for property in Zimbabwe?</div>
          <p style={{ color: "var(--muted)", fontSize: 14, marginBottom: 16 }}>Browse verified listings with Trust Scores across Harare and beyond.</p>
          <Link href="/listings" style={{ padding: "10px 20px", background: "var(--navy)", color: "var(--cream)", borderRadius: 8, textDecoration: "none", fontWeight: 700, fontSize: 14 }}>Browse listings</Link>
        </div>
      </div>
    </div>
  )
}